import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import {
  View,
  StyleSheet,
  FlatList,
  RefreshControl,
  TouchableOpacity,
  Platform,
  Alert,
  Linking,
} from 'react-native';
import {
  Text,
  Card,
  Button,
  ActivityIndicator,
  Dialog,
  Portal,
  TextInput,
} from 'react-native-paper';
import axios from 'axios';

const RAILWAY_URL = 'https://radyohub-production.up.railway.app';
const API_BASE_URL = `${RAILWAY_URL}/api`;

export default function GameRoomList({
  onClose,
  onSelectRoom,
  userName = 'Kullanıcı',
  gameId = 'category-battle',
  tokens = 0,
  spendTokens,
  showRewardedAd,
  isAdsenseConfigured = false,
}) {
  // Debug: gameId prop'unu kontrol et
  if (Platform.OS === 'web') {
    console.log('[GameRoomList] gameId prop:', gameId);
  }

  const [gameMeta, setGameMeta] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newRoomName, setNewRoomName] = useState('');
  const isCreatingRoomRef = useRef(false);

  const loadRooms = useCallback(async () => {
    try {
      setError(null);
      const response = await axios.get(`${API_BASE_URL}/games/${gameId}/rooms`, {
        timeout: 5000,
      });
      setGameMeta(response.data?.game || null);
      const normalizedRooms = (response.data?.rooms || []).map((room) => ({
        ...room,
        gameType: gameId,
      }));
      setRooms(normalizedRooms);
    } catch (err) {
      console.error('Oyun odaları yüklenemedi:', err);
      setError('Oyun odaları yüklenemedi. Tekrar dene!');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [gameId]);

  const createRoom = useCallback(async () => {
    if (!['pisti', 'batak', 'uno', 'okey'].includes(gameId)) {
      return;
    }

    const trimmedName = newRoomName.trim();
    if (!trimmedName) {
      Alert.alert('Hata', 'Lütfen oda adı girin');
      return;
    }
    if (trimmedName.length < 3) {
      Alert.alert('Hata', 'Oda adı en az 3 karakter olmalıdır.');
      return;
    }

    // Sosyal kredi / enerji kontrolü: mobilde oyun odası oluşturmak için 1 kredi gerekir
    if (tokens < 1) {
      if (Platform.OS === 'web') {
        // Web'de AdSense yapılandırılmamışsa veya reklam mekanizması yoksa, enerji kontrolünü bypass et
        if (!isAdsenseConfigured || !showRewardedAd) {
          console.log(
            '[GameRoomList] Web - AdSense yok, enerji kontrolü bypass edildi (oda oluşturma)'
          );
          // Enerji harcamasını aşağıda da atlayacağız
        } else {
          Alert.alert(
            'Düşük Enerji',
            'Oyun odası oluşturmak için 1 Sosyal Kredi (Enerji) gerekir.\n\nReklam izleyerek enerjini yükseltebilirsin.',
            [
              {
                text: 'İptal',
                style: 'cancel',
                onPress: () => {
                  setShowCreateDialog(false);
                },
              },
              {
                text: 'Reklam İzle',
                onPress: () => {
                  setShowCreateDialog(false);
                  if (typeof showRewardedAd === 'function') {
                    showRewardedAd();
                  }
                },
              },
            ]
          );
          return;
        }
      } else {
        // Mobil cihazlarda mutlaka 1 sosyal kredi gerek
        Alert.alert(
          'Düşük Enerji',
          'Oyun odası oluşturmak için 1 Sosyal Kredi (Enerji) gerekir.\n\nReklam izleyerek enerjini yükseltebilirsin.',
          [
            {
              text: 'İptal',
              style: 'cancel',
              onPress: () => {
                setShowCreateDialog(false);
              },
            },
            {
              text: 'Reklam İzle',
              onPress: () => {
                setShowCreateDialog(false);
                if (typeof showRewardedAd === 'function') {
                  showRewardedAd();
                }
              },
            },
          ]
        );
        return;
      }
    }

    if (isCreatingRoomRef.current) {
      return;
    }

    isCreatingRoomRef.current = true;
    try {
      let endpoint = `${API_BASE_URL}/games/pisti/rooms`;
      if (gameId === 'batak') {
        endpoint = `${API_BASE_URL}/games/batak/rooms`;
      } else if (gameId === 'uno') {
        endpoint = `${API_BASE_URL}/games/uno/rooms`;
      } else if (gameId === 'okey') {
        endpoint = `${API_BASE_URL}/games/okey/rooms`;
      }
      const response = await axios.post(
        endpoint,
        {
          roomName: trimmedName,
          creatorName: userName,
        },
        { timeout: 10000 }
      );

      if (response.data?.roomId) {
        // Başarılı oluşturma sonrası sosyal kredi harca (mobilde zorunlu, web'de isteğe bağlı)
        if (Platform.OS === 'web') {
          if (!isAdsenseConfigured || !showRewardedAd) {
            console.log(
              '[GameRoomList] Web - AdSense yok, enerji harcaması atlandı (oda oluşturma)'
            );
          } else if (typeof spendTokens === 'function') {
            const spent = await spendTokens(1);
            if (!spent) {
              console.warn('[GameRoomList] Web - sosyal kredi harcanamadı (oda oluşturma)');
              Alert.alert(
                'Uyarı',
                'Sosyal kredi güncellenemedi. Lütfen kredini kontrol et.'
              );
            }
          }
        } else if (typeof spendTokens === 'function') {
          const spent = await spendTokens(1);
          if (!spent) {
            console.warn('[GameRoomList] Mobil - sosyal kredi harcanamadı (oda oluşturma)');
            Alert.alert(
              'Uyarı',
              'Sosyal kredi güncellenemedi. Lütfen kredini kontrol et.'
            );
          }
        }

        // Yeni odayı listeye ekle ve otomatik seç
        const newRoom = {
          roomId: response.data.roomId,
          roomName: response.data.roomName,
          participantCount: response.data.participantCount || 1,
          maxPlayers: response.data.maxPlayers || (gameId === 'uno' ? 6 : gameId === 'okey' ? 4 : 4),
          status: response.data.status || 'WAITING',
          createdAt: response.data.createdAt,
          gameType: gameId,
        };
        console.log('[GameRoomList] Yeni oda oluşturuldu:', newRoom);
        // Odayı listeye ekle ve hemen odaları yeniden yükle (diğer kullanıcılar için)
        setRooms(prev => [newRoom, ...prev]);
        // Hemen odaları yeniden yükle ki diğer kullanıcılar görebilsin
        setTimeout(() => {
          loadRooms();
        }, 500);
        setShowCreateDialog(false);
        setNewRoomName('');
        // Yeni odayı otomatik seç
        onSelectRoom?.({
          ...newRoom,
          gameId,
          userName,
        });
      }
    } catch (err) {
      console.error('Oda oluşturma hatası:', err);
      Alert.alert('Hata', err.response?.data?.error || 'Oda oluşturulamadı. Tekrar dene!');
    } finally {
      isCreatingRoomRef.current = false;
    }
  }, [gameId, newRoomName, userName, onSelectRoom]);

  useEffect(() => {
    loadRooms();
    const interval = setInterval(loadRooms, 5000);
    return () => clearInterval(interval);
  }, [loadRooms]);

  const handleRefresh = useCallback(() => {
    setRefreshing(true);
    loadRooms();
  }, [loadRooms]);

  const handleRoomPress = useCallback((room) => {
    if (!room) {
      return;
    }
    onSelectRoom?.({
      ...room,
      gameId,
      userName,
    });
  }, [gameId, onSelectRoom, userName]);

  const handleInvite = useCallback(() => {
    const gameNames = {
      'pisti': 'Pişti',
      'batak': 'Batak',
      'uno': 'UNO',
      'okey': 'OKEY',
    };
    const gameName = gameNames[gameId] || 'oyun';
    
    // Basit Play Store URL'si
    const appUrl = 'https://play.google.com/store/apps/details?id=com.tardasoft.samata';
    const message = `Gel ${gameName} oynayalım. ${appUrl}`;
    const whatsappUrl = `whatsapp://send?text=${encodeURIComponent(message)}`;
    
    Linking.canOpenURL(whatsappUrl)
      .then((supported) => {
        if (supported) {
          return Linking.openURL(whatsappUrl);
        } else {
          // WhatsApp yüklü değilse, web versiyonunu aç
          const webWhatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
          return Linking.openURL(webWhatsappUrl);
        }
      })
      .catch((err) => {
        console.error('WhatsApp açma hatası:', err);
        Alert.alert('Hata', 'WhatsApp açılamadı. Lütfen WhatsApp\'ın yüklü olduğundan emin olun.');
      });
  }, [gameId]);

  const heroStats = useMemo(() => {
    if (['pisti', 'batak', 'uno'].includes(gameId)) {
      return [
        { label: 'Aktif Oda', value: gameMeta?.activeRooms || 0 },
        { label: 'Toplam Oyuncu', value: gameMeta?.totalPlayers || 0 },
        { label: 'Durum', value: gameMeta?.activeRooms ? 'Oyun Hazır' : 'Oyuncu Bekleniyor' },
      ];
    }
    return [
      { label: 'Harf', value: gameMeta?.currentLetter || '–' },
      { label: 'Kategori', value: gameMeta?.currentCategory || 'Hazırlanıyor' },
      { label: 'Durum', value: gameMeta?.phase === 'COLLECTING' ? 'Tur Devam Ediyor' : 'Ara Verildi' },
    ];
  }, [gameMeta, gameId]);

  const renderRoom = ({ item }) => {
    const isGameStarted = item.status && item.status !== 'WAITING';
    const isPisti = gameId === 'pisti';
    const isBatak = gameId === 'batak';
    const isUno = gameId === 'uno';
    const isOkey = gameId === 'okey';
    const isGameRoom = isPisti || isBatak || isUno || isOkey;
    const isDisabled = isGameStarted && isGameRoom;
    
    return (
      <Card 
        style={[
          styles.roomCard, 
          isDisabled && styles.roomCardDisabled
        ]} 
        onPress={() => !isDisabled && handleRoomPress(item)}
        disabled={isDisabled}
      >
        <Card.Content>
          <View style={styles.roomRow}>
            <View style={{ flex: 1 }}>
              <Text style={[styles.roomName, isDisabled && styles.roomNameDisabled]}>
                {item.roomName}
              </Text>
              <Text style={styles.roomMeta}>
                {item.participantCount} oyuncu aktif
                {isDisabled && ' • Oyun Başlamış'}
              </Text>
            </View>
            <Button 
              mode="contained" 
              onPress={() => {
                if (isDisabled) {
                  Alert.alert('Oyun Başlamış', 'Bu oyun başlamış, yeni oyuncu kabul edilmiyor.');
                } else {
                  handleRoomPress(item);
                }
              }}
              disabled={isDisabled}
              style={isDisabled && styles.buttonDisabled}
            >
              {isDisabled ? 'Oyun Başlamış' : 'Odaya Gir'}
            </Button>
          </View>
        </Card.Content>
      </Card>
    );
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          onPress={onClose}
          style={styles.closeButton}
        >
          <Text style={styles.closeText}>✕</Text>
        </TouchableOpacity>
        <View style={styles.headerInfo}>
          <Text style={styles.gameTitle}>
            {gameMeta?.title ||
              (gameId === 'pisti' ? 'Pişti' :
                gameId === 'batak' ? 'Batak' :
                gameId === 'uno' ? 'UNO' :
                gameId === 'okey' ? 'OKEY' :
                gameId === 'mystery-word-hunt' ? 'Gizemli Kelime Avı' : 'Kategori Savaşı')}
          </Text>
          <Text style={styles.gameSubtitle}>
            {gameMeta?.description ||
              (gameId === 'pisti'
                ? 'Klasik kart oyunu - 2-4 oyuncu ile oyna!'
                : gameId === 'batak'
                ? 'Koz maça, 4 oyunculu strateji kart oyunu'
                : gameId === 'uno'
                ? 'Renkleri eşleştir, özel kartlarla rakiplerini yavaşlat!'
                : gameId === 'okey'
                ? 'Klasik Türk oyunu - 2-4 oyuncu ile oyna!'
                : gameId === 'mystery-word-hunt'
                ? 'Kelimeyi bul enerjini yükselt!'
                : 'Yapay zekaya karşı kelime kapmaca!')}
          </Text>
        </View>
      </View>

      <Card style={styles.heroCard}>
        <Card.Content>
          <Text style={styles.heroTitle}>Oyun Özeti</Text>
          <Text style={styles.heroDescription}>
            {gameId === 'pisti'
              ? 'Klasik kart oyunu - eşleş, kartları kap!'
              : gameId === 'batak'
              ? 'Koz maça! 4 oyuncu ile ihale ve el alma mücadelesi.'
              : gameId === 'uno'
              ? 'UNO destesiyle renkleri eşleştir, özel kartlarla avantaj kazan.'
              : gameId === 'okey'
              ? 'OKEY taşlarıyla set ve seri yap, elini bitir ve kazan!'
              : gameId === 'mystery-word-hunt'
              ? 'Kelimeyi bul enerjini yükselt!'
              : gameId === 'category-battle'
              ? 'Kategoriye uygun kelimeyi bul, enerjiyi kap!'
              : 'Yapay zeka harfi ve kategoriyi seçer. Her turda benzersiz kelimelerle puan topla!'}
          </Text>
          <View style={styles.heroStatsRow}>
            {heroStats.map((stat) => (
              <View key={stat.label} style={styles.heroStat}>
                <Text style={styles.heroStatLabel}>{stat.label}</Text>
                <Text style={styles.heroStatValue}>{stat.value}</Text>
              </View>
            ))}
          </View>
        </Card.Content>
      </Card>

      {error && (
        <Card style={styles.errorCard}>
          <Card.Content>
            <Text style={styles.errorText}>{error}</Text>
            <Button mode="outlined" onPress={loadRooms} style={{ marginTop: 8 }}>
              Yeniden Dene
            </Button>
          </Card.Content>
        </Card>
      )}

      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" />
          <Text style={styles.loadingText}>Oyun odaları yükleniyor...</Text>
        </View>
      ) : (
        <FlatList
          data={rooms}
          renderItem={renderRoom}
          keyExtractor={(item) => item.roomId}
          contentContainerStyle={styles.listContent}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={handleRefresh}
              tintColor="#FF4444"
            />
          }
          ListEmptyComponent={
            <View style={styles.emptyState}>
              <Text style={styles.emptyTitle}>Henüz aktif oda yok</Text>
              <Text style={styles.emptySub}>
                {gameId === 'pisti'
                  ? 'İlk Pişti odasını oluşturmak ister misin?'
                  : gameId === 'batak'
                  ? 'Batak masası kurmak için ilk adımı at!'
                  : gameId === 'uno'
                  ? 'UNO kartlarını karıştır, yeni masa seni bekliyor!'
                  : gameId === 'okey'
                  ? 'OKEY taşlarını karıştır, yeni masa seni bekliyor!'
                  : 'Hakem odası hazırlanırken biraz bekle!'}
              </Text>
            </View>
          }
        />
      )}

      {/* Pişti, UNO ve Batak için oda oluşturma FAB ve Davet Et butonu */}
      {(gameId === 'pisti' || gameId === 'uno' || gameId === 'batak') && (
        <>
          <View style={styles.fabContainer}>
            <TouchableOpacity
              style={styles.fabInvite}
              onPress={handleInvite}
              activeOpacity={0.7}
            >
              <Text style={styles.fabIconText}>👥</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.fab}
              onPress={() => setShowCreateDialog(true)}
              activeOpacity={0.7}
            >
              <Text style={styles.fabIconText}>＋</Text>
            </TouchableOpacity>
          </View>
          <Portal>
            <Dialog visible={showCreateDialog} onDismiss={() => setShowCreateDialog(false)}>
              <Dialog.Title>
                {gameId === 'batak'
                  ? 'Yeni Batak Masası Oluştur'
                  : gameId === 'uno'
                  ? 'Yeni UNO Masası Oluştur'
                  : 'Yeni Pişti Odası Oluştur'}
              </Dialog.Title>
              <Dialog.Content>
                <TextInput
                  label="Oda Adı"
                  value={newRoomName}
                  onChangeText={setNewRoomName}
                  mode="outlined"
                  autoFocus
                  maxLength={50}
                />
              </Dialog.Content>
              <Dialog.Actions>
                <Button onPress={() => setShowCreateDialog(false)}>İptal</Button>
                <Button onPress={createRoom} mode="contained">Oluştur</Button>
              </Dialog.Actions>
            </Dialog>
          </Portal>
        </>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0F1115',
    ...(Platform.OS === 'web' ? {
      minHeight: '100vh',
      width: '100%',
    } : {}),
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingTop: 48,
    paddingBottom: 16,
  },
  closeButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#1F1F28',
    marginRight: 12,
  },
  closeText: {
    fontSize: 20,
    color: '#FFFFFF',
  },
  headerInfo: {
    flex: 1,
  },
  gameTitle: {
    color: '#FFFFFF',
    fontSize: 22,
    fontWeight: '700',
  },
  gameSubtitle: {
    color: '#B0B7C3',
    fontSize: 14,
    marginTop: 4,
  },
  heroCard: {
    marginHorizontal: 16,
    marginBottom: 12,
    backgroundColor: '#151A22',
    borderRadius: 16,
  },
  heroTitle: {
    color: '#FFFFFF',
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 4,
  },
  heroDescription: {
    color: '#B0B7C3',
    fontSize: 14,
    marginBottom: 12,
  },
  heroStatsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  heroStat: {
    flex: 1,
  },
  heroStatLabel: {
    color: '#8C92A4',
    fontSize: 12,
  },
  heroStatValue: {
    color: '#FFFFFF',
    fontSize: 18,
    fontWeight: '700',
    marginTop: 4,
  },
  listContent: {
    paddingHorizontal: 16,
    paddingBottom: 32,
  },
  roomCardDisabled: {
    opacity: 0.6,
    backgroundColor: '#1A1A1A',
  },
  roomCard: {
    backgroundColor: '#151A22',
    borderRadius: 16,
    marginBottom: 12,
  },
  roomNameDisabled: {
    color: '#8C92A4',
    textDecorationLine: 'line-through',
  },
  buttonDisabled: {
    opacity: 0.5,
  },
  roomRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  roomName: {
    color: '#FFFFFF',
    fontSize: 18,
    fontWeight: '600',
  },
  roomMeta: {
    color: '#8C92A4',
    fontSize: 13,
    marginTop: 4,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    color: '#8C92A4',
    marginTop: 8,
  },
  emptyState: {
    paddingVertical: 48,
    alignItems: 'center',
  },
  emptyTitle: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  emptySub: {
    color: '#8C92A4',
    fontSize: 14,
    marginTop: 4,
    textAlign: 'center',
    paddingHorizontal: 32,
  },
  errorCard: {
    marginHorizontal: 16,
    marginBottom: 12,
    backgroundColor: '#2A1E1E',
  },
  errorText: {
    color: '#FF7B7B',
  },
  fabContainer: {
    position: 'absolute',
    left: 16,
    bottom: 16,
    flexDirection: 'row',
    gap: 12,
    alignItems: 'flex-end',
    ...(Platform.OS === 'web' ? {
      bottom: 20,
      zIndex: 1000,
    } : {
      paddingBottom: 8,
    }),
  },
  fabButtonWrapper: {
    alignItems: 'center',
    gap: 4,
    flexDirection: 'column',
  },
  fab: {
    backgroundColor: '#4CAF50',
    width: 44,
    height: 44,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  fabInvite: {
    backgroundColor: '#FF6B35',
    width: 44,
    height: 44,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  fabIconText: {
    color: '#FFFFFF',
    fontSize: 20,
  },
  fabLabel: {
    color: '#FFFFFF',
    fontSize: 10,
    fontWeight: '600',
    textAlign: 'center',
  },
});


